import { useResolvedDesignSystemComponent } from 'ext-ui/design-system/DesignSystemChannel'
import GeneratedLatestPill, {
  PillProps as GeneratedPillProps,
} from 'ext-ui/design-system/generated/latest/components/Pill/Pill'
import GeneratedLivePill from 'ext-ui/design-system/generated/live/components/Pill/Pill'
import React, { ReactElement } from 'react'

import PillLegacy, { PillProps } from './PillLegacy'

type PublicPillColor = NonNullable<PillProps['color']>
type GeneratedPillType = NonNullable<GeneratedPillProps['type']>
type PublicPillSize = NonNullable<PillProps['size']>
type GeneratedPillSize = NonNullable<GeneratedPillProps['size']>

const generatedTypeByPublicColor: Partial<
  Record<PublicPillColor, GeneratedPillType>
> = {
  highlight: 'highlight',
  new: 'aiNew',
  pending: 'pending',
  saas: 'saas',
  severe: 'severe',
}

const generatedSizeByPublicSize: Partial<
  Record<PublicPillSize, GeneratedPillSize>
> = {
  large: 'large',
  medium: 'medium',
  small: 'small',
}

function generatedTypeFor(color: PillProps['color']): GeneratedPillType | null {
  return color ? (generatedTypeByPublicColor[color] ?? null) : 'default'
}

function generatedSizeFor(size: PillProps['size']): GeneratedPillSize | null {
  return generatedSizeByPublicSize[size ?? 'medium'] ?? null
}

function fallbackChannelMarker(channel: 'live' | 'latest'): string {
  return channel === 'latest' ? 'latest-live-fallback' : 'live-legacy-fallback'
}

function renderDesignSystemPill(
  Component: typeof GeneratedLivePill,
  channel: 'live' | 'latest',
  props: PillProps,
): ReactElement {
  const generatedType = generatedTypeFor(props.color)
  const generatedSize = generatedSizeFor(props.size)
  const shouldUseLegacyFallback =
    !generatedType || !generatedSize || Boolean(props.onClick)

  if (shouldUseLegacyFallback) {
    return (
      <PillLegacy
        {...props}
        data-design-system-channel={fallbackChannelMarker(channel)}
      />
    )
  }

  const { color: _color, size: _size, ...rest } = props

  return (
    <Component
      {...(rest as Omit<GeneratedPillProps, 'size' | 'type'>)}
      data-design-system-channel={channel}
      size={generatedSize}
      type={generatedType}
    />
  )
}

function LivePill(props: PillProps): ReactElement {
  return renderDesignSystemPill(GeneratedLivePill, 'live', props)
}

function LatestPill(props: PillProps): ReactElement {
  return renderDesignSystemPill(GeneratedLatestPill, 'latest', props)
}

function Pill(props: PillProps): ReactElement {
  const ResolvedPill = useResolvedDesignSystemComponent(LivePill, LatestPill)

  return <ResolvedPill {...props} />
}

export default Pill
export type { PillProps }
